import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import {
  organizationMembershipEnrollMissingRoute,
  organizationMembershipOverviewRoute,
} from "network/api-routes";
import { membershipCurrencySymbol } from "utils/membership-ui-helpers";

const STATUS_ORDER = ["active", "suspended", "deceased", "banned"];

const formatMoney = (amount, currencyCode) => {
  const symbol = membershipCurrencySymbol(currencyCode);
  const value = amount == null || amount === "" ? "—" : Number(amount).toLocaleString();
  return symbol ? `${symbol} ${value}` : String(value);
};

const MetricCard = ({ label, value, helper }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <RevaText variant="overline" color="text.secondary">
        {label}
      </RevaText>
      <RevaText variant="h5" sx={{ mt: 0.5 }}>
        {value}
      </RevaText>
      {helper ? (
        <RevaText variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {helper}
        </RevaText>
      ) : null}
    </CardContent>
  </Card>
);

/**
 * Overview metrics for org membership dashboard.
 * Pass `refreshKey` (number) and bump it to reload after save/import.
 * Optional `onEnrolled` fires after gap enroll so parent can refresh payments too.
 */
const OrganizationMembershipOverviewCards = ({ refreshKey = 0, onEnrolled }) => {
  const intl = useIntl();
  const { user, selectedOrganization } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [overview, setOverview] = useState(null);

  const loadOverview = useCallback(() => {
    if (!selectedOrganization?.id) return;
    setLoading(true);
    performRequest("get", organizationMembershipOverviewRoute(selectedOrganization.id), {}, true, user)
      .then((res) => setOverview(res.data || {}))
      .catch((error) =>
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "org.membership.overview.loadError" }),
          { variant: "error" },
        ),
      )
      .finally(() => setLoading(false));
  }, [enqueueSnackbar, intl, selectedOrganization, user]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview, refreshKey]);

  const enrollMissing = () => {
    if (!selectedOrganization?.id) return;
    setEnrolling(true);
    performRequest("post", organizationMembershipEnrollMissingRoute(selectedOrganization.id), {}, true, user)
      .then((res) => {
        const created = res.data?.created ?? 0;
        enqueueSnackbar(
          intl.formatMessage({ id: "org.membership.enrollMissingSuccess" }, { created }),
          { variant: "success" },
        );
        loadOverview();
        if (typeof onEnrolled === "function") onEnrolled();
      })
      .catch((error) =>
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "org.membership.enrollMissingError" }),
          { variant: "error" },
        ),
      )
      .finally(() => setEnrolling(false));
  };

  if (!selectedOrganization) return null;

  if (loading && !overview) {
    return (
      <Box py={2}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const currencyCode = overview?.currency_code || null;
  const byStatus = overview?.memberships_by_status || {};
  const statusSummary = STATUS_ORDER.filter((status) => (byStatus[status] ?? 0) > 0 || status === "active")
    .map((status) =>
      intl.formatMessage(
        { id: "org.membership.overview.statusCount" },
        {
          status: intl.formatMessage({ id: `org.membership.status.${status}` }),
          count: byStatus[status] ?? 0,
        },
      ),
    )
    .join(" · ");

  const year = overview?.calendar_year;
  const showEnrollGap =
    Boolean(overview?.amounts_configured) && Number(overview?.members_without_membership || 0) > 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap">
        <RevaText variant="h6">{intl.formatMessage({ id: "org.membership.sections.cards" })}</RevaText>
        {loading ? <CircularProgress size={18} /> : null}
        {showEnrollGap ? (
          <Button variant="outlined" size="small" onClick={enrollMissing} disabled={enrolling}>
            {intl.formatMessage({ id: "org.membership.enrollMissing" })}
          </Button>
        ) : null}
      </Stack>

      {overview && overview.amounts_configured === false ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {intl.formatMessage({ id: "org.membership.overview.configureCta" })}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            label={intl.formatMessage({ id: "org.membership.overview.byStatus" })}
            value={statusSummary || "—"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            label={intl.formatMessage({ id: "org.membership.overview.withoutMembership" })}
            value={overview?.members_without_membership ?? "—"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            label={intl.formatMessage({ id: "org.membership.overview.toCollectMonth" })}
            value={formatMoney(overview?.to_collect_month, currencyCode)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            label={intl.formatMessage({ id: "org.membership.overview.toCollectYear" })}
            value={formatMoney(overview?.to_collect_year, currencyCode)}
            helper={year ? intl.formatMessage({ id: "org.membership.overview.yearHint" }, { year }) : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            label={intl.formatMessage({ id: "org.membership.overview.collectedMonth" })}
            value={formatMoney(overview?.collected_month, currencyCode)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            label={intl.formatMessage({ id: "org.membership.overview.collectedYear" })}
            value={formatMoney(overview?.collected_year, currencyCode)}
            helper={year ? intl.formatMessage({ id: "org.membership.overview.yearHint" }, { year }) : undefined}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrganizationMembershipOverviewCards;
